/**
 * Object Finder Main Entry Point
 *
 * Initializes the Object Finder application and sets up all event handlers.
 * This is the main script loaded by object-finder.html.
 */

import ObjectFinder from './objectFinder.js';
import ResultsDisplay from './resultsDisplay.js';
import SessionAnalytics from './sessionAnalytics.js';
import URLSerializer from './urlSerializer.js';
import ConfigurationManager from './configurationManager.js';
import BookmarkManager from './bookmarkManager.js';
import CrossPageNavigator from './crossPageNavigator.js';

// Global application instances
let objectFinder = null;
let resultsDisplay = null;
let sessionAnalytics = null;
let urlSerializer = null;
let configManager = null;
let bookmarkManager = null;
let crossPageNavigator = null;

// Store last search data for sharing
let lastSearchParams = null;
let lastSearchResults = null;

/**
 * Initialize the Object Finder application
 */
async function initializeObjectFinder() {
    try {
        objectFinder = new ObjectFinder();
        resultsDisplay = new ResultsDisplay();
        sessionAnalytics = new SessionAnalytics();
        urlSerializer = new URLSerializer();
        configManager = new ConfigurationManager();
        bookmarkManager = new BookmarkManager();
        crossPageNavigator = new CrossPageNavigator(objectFinder.calculator);

        // Until the real handler is attached, keep Search from doing a native
        // form submit (which would reload the page mid catalogue import)
        const form = document.getElementById('objectFinderForm');
        const blockSubmit = (e) => e.preventDefault();
        if (form) form.addEventListener('submit', blockSubmit);

        let reloadingCatalogue = false;
        const success = await objectFinder.initialize({
            onDataReload: () => {
                reloadingCatalogue = true;
                updateMainLoadingMessage('Updating the object catalogue (one-time, about 30 seconds)...');
                objectFinder.showLoading(true);
            }
        });
        if (form) form.removeEventListener('submit', blockSubmit);
        if (reloadingCatalogue) objectFinder.showLoading(false);

        if (success) {
            resultsDisplay.initialize();
            setupEventListeners();
            setupUI();

            // Check for URL parameters and auto-load if present
            checkAndLoadFromURL();

            // Expose globals for access from other components
            window.objectFinder = objectFinder;
            window.resultsDisplay = resultsDisplay;
            window.sessionAnalytics = sessionAnalytics;
            window.urlSerializer = urlSerializer;
            window.configManager = configManager;
            window.bookmarkManager = bookmarkManager;
            window.crossPageNavigator = crossPageNavigator;
            window.timelineViz = objectFinder.timelineVisualization;
            window.timelineControls = objectFinder.timelineControls;
            window.skyPreview = objectFinder.skyPreview;

            console.log('Object Finder ready');
        } else {
            console.error('Failed to initialize Object Finder');
            showError('Failed to initialize Object Finder. Please refresh the page.');
        }
    } catch (error) {
        console.error('Initialization error:', error);
        showError('Initialization error: ' + error.message);
    }
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
    // Form submission
    const form = document.getElementById('objectFinderForm');
    if (form) {
        form.addEventListener('submit', handleFormSubmit);
    }

    // Direction change notifications
    if (objectFinder.directionSelector) {
        objectFinder.directionSelector.onDirectionChange((direction) => {
            // Update sky preview when direction changes
            updateSkyPreview();
        });
    }

    // Location and time change notifications
    if (objectFinder.locationTimeSelector) {
        objectFinder.locationTimeSelector.onLocationChange((location) => {
            // Location updated
        });

        objectFinder.locationTimeSelector.onDateChange((date) => {
            // Date updated
        });
    }

    // Sky preview parameter listeners
    setupSkyPreviewListeners();

    // Close error alert
    const errorAlert = document.getElementById('errorAlert');
    if (errorAlert) {
        errorAlert.addEventListener('click', () => {
            errorAlert.style.display = 'none';
        });
    }

    // Save configuration button
    const saveConfigBtn = document.getElementById('saveConfigBtn');
    if (saveConfigBtn) {
        saveConfigBtn.addEventListener('click', handleSaveConfig);
    }

    // Load configuration button
    const loadConfigBtn = document.getElementById('loadConfigBtn');
    if (loadConfigBtn) {
        loadConfigBtn.addEventListener('click', handleLoadConfig);
    }

    // Share search button
    const shareSearchBtn = document.getElementById('shareSearchBtn');
    if (shareSearchBtn) {
        shareSearchBtn.addEventListener('click', handleShareSearch);
    }

    // Save config confirm button (in modal)
    const saveConfigConfirmBtn = document.getElementById('saveConfigConfirmBtn');
    if (saveConfigConfirmBtn) {
        saveConfigConfirmBtn.addEventListener('click', confirmSaveConfig);
    }

    // Copy URL button (in share modal)
    const copyUrlBtn = document.getElementById('copyUrlBtn');
    if (copyUrlBtn) {
        copyUrlBtn.addEventListener('click', copyShareUrl);
    }

    // Clear all configs button
    const clearAllConfigsBtn = document.getElementById('clearAllConfigsBtn');
    if (clearAllConfigsBtn) {
        clearAllConfigsBtn.addEventListener('click', handleClearAllConfigs);
    }
}

/**
 * Set up sky preview parameter listeners
 */
function setupSkyPreviewListeners() {
    // List of parameter input IDs that affect the preview
    const parameterIds = [
        'targetAzimuth', 'azimuthSlider',
        'minAltitude', 'minAltitudeSlider',
        'maxAltitude', 'maxAltitudeSlider',
        'fieldOfView', 'fovSlider'
    ];

    // Add input event listeners to all parameters
    parameterIds.forEach(paramId => {
        const element = document.getElementById(paramId);
        if (element) {
            element.addEventListener('input', updateSkyPreview);
        }
    });
}

/**
 * Update sky preview with current parameter values
 */
function updateSkyPreview() {
    if (!objectFinder || !objectFinder.skyPreview || !objectFinder.skyPreview.isInitialized) {
        return;
    }

    // Gather current parameter values
    const azimuthInput = document.getElementById('targetAzimuth');
    const minAltInput = document.getElementById('minAltitude');
    const maxAltInput = document.getElementById('maxAltitude');
    const fovInput = document.getElementById('fieldOfView');

    if (!azimuthInput || !minAltInput || !maxAltInput || !fovInput) {
        return;
    }

    const params = {
        azimuth: isNaN(parseFloat(azimuthInput.value)) ? 180 : parseFloat(azimuthInput.value),
        minAltitude: isNaN(parseFloat(minAltInput.value)) ? 30 : parseFloat(minAltInput.value),
        maxAltitude: isNaN(parseFloat(maxAltInput.value)) ? 70 : parseFloat(maxAltInput.value),
        fieldOfView: isNaN(parseFloat(fovInput.value)) ? 15 : parseFloat(fovInput.value)
    };

    // Update the preview
    objectFinder.skyPreview.update(params);
}

/**
 * Set up UI elements
 */
function setupUI() {
    // Set current date as default
    const dateInput = document.getElementById('observationDate');
    if (dateInput && !dateInput.value) {
        const today = new Date();
        dateInput.value = today.toISOString().split('T')[0];
    }

    // Initialize tooltips if Bootstrap is available
    if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new bootstrap.Tooltip(tooltipTriggerEl);
        });
    }

}

/**
 * Handle form submission
 * @param {Event} event - Form submit event
 */
async function handleFormSubmit(event) {
    event.preventDefault();

    try {
        // Collect form data
        const formData = collectFormData();

        // Validate form data
        if (!validateFormData(formData)) {
            return;
        }

        console.log('Searching for objects...', formData);

        const results = await objectFinder.findObjects(formData);
        console.log(`Found ${results?.length || 0} objects`);

        // Debug: Test with a simple coordinate check
        if (results.length === 0) {
            console.warn('No objects found! Testing coordinate conversion...');
            console.log('Search parameters used:', formData);

            // Test coordinate ranges we're looking for
            console.log('Expected: Looking for objects with RA ~',
                (formData.observationDate ? new Date(formData.observationDate + 'T00:00:00').getTime() : Date.now()) / 86400000 + 2440587.5,
                'degrees LST area');
        }

        const statistics = objectFinder.getRankingStatistics();

        // Store search data for sharing
        lastSearchParams = formData;
        lastSearchResults = results;

        // Enable share button now that we have results
        const shareBtn = document.getElementById('shareSearchBtn');
        if (shareBtn) shareBtn.disabled = false;

        // Update URL with search parameters (without page reload)
        urlSerializer.updateURL(formData, results.slice(0, 10));

        // Sequential loading: 1) Table, 2) Timeline
        console.log('Step 1/2: Loading results table...');
        updateMainLoadingMessage('Loading results table...');
        showLoadingStep('Loading results table...', 1);
        await resultsDisplay.displayResults(results, formData, statistics);

        console.log('Step 2/2: Loading timeline visualization...');
        updateMainLoadingMessage('Loading timeline visualization...');
        showLoadingStep('Loading timeline visualization...', 2);
        await objectFinder.generateTimelineVisualization(formData);

        console.log('All visualizations loaded successfully');
        // Hide both main loading and step indicators
        objectFinder.showLoading(false);
        hideLoadingSteps();

        // Scroll to results section so user knows results are available
        const resultsSection = document.getElementById('resultsSection');
        if (resultsSection) {
            setTimeout(() => {
                resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }

    } catch (error) {
        console.error('Error during object search:', error);
        showError('Search failed: ' + error.message);
        // Hide both main loading and step indicators on error
        objectFinder.showLoading(false);
        hideLoadingSteps();
    }
}

/**
 * Collect form data
 * @returns {Object} Form data
 */
function collectFormData() {
    // Get data from components if available, otherwise fall back to form inputs
    const location = objectFinder?.locationTimeSelector?.getCurrentLocation() || {
        latitude: parseFloat(document.getElementById('observerLatitude')?.value) || 42.0,
        longitude: parseFloat(document.getElementById('observerLongitude')?.value) || -6.0
    };

    const date = objectFinder?.locationTimeSelector?.getCurrentDate() ||
                document.getElementById('observationDate')?.value ||
                new Date().toISOString().split('T')[0];

    const direction = objectFinder?.directionSelector?.getCurrentDirection() || {
        azimuth: isNaN(parseFloat(document.getElementById('targetAzimuth')?.value)) ? 180 : parseFloat(document.getElementById('targetAzimuth')?.value),
        minAltitude: isNaN(parseFloat(document.getElementById('minAltitude')?.value)) ? 30 : parseFloat(document.getElementById('minAltitude')?.value),
        maxAltitude: isNaN(parseFloat(document.getElementById('maxAltitude')?.value)) ? 70 : parseFloat(document.getElementById('maxAltitude')?.value),
        fieldOfView: isNaN(parseFloat(document.getElementById('fieldOfView')?.value)) ? 15 : parseFloat(document.getElementById('fieldOfView')?.value)
    };

    return {
        // Location & Time
        latitude: location.latitude,
        longitude: location.longitude,
        observationDate: date,

        // Sky Direction
        azimuth: direction.azimuth,
        minAltitude: direction.minAltitude,
        maxAltitude: direction.maxAltitude,
        fieldOfView: direction.fieldOfView,

        // Filters
        minMagnitude: parseFloat(document.getElementById('minMagnitude')?.value) || 0,
        maxMagnitude: parseFloat(document.getElementById('maxMagnitude')?.value) || 12,
        minDuration: parseFloat(document.getElementById('minDuration')?.value) || 2,
        moonAvoidance: parseFloat(document.getElementById('moonAvoidance')?.value) || 30
    };
}

/**
 * Validate form data
 * @param {Object} formData - Form data to validate
 * @returns {boolean} True if valid
 */
function validateFormData(formData) {
    const errors = [];

    // Basic validation
    if (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90) {
        errors.push('Latitude must be between -90 and 90 degrees');
    }

    if (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180) {
        errors.push('Longitude must be between -180 and 180 degrees');
    }

    if (!formData.observationDate) {
        errors.push('Observation date is required');
    }

    if (formData.minAltitude >= formData.maxAltitude) {
        errors.push('Minimum altitude must be less than maximum altitude');
    }

    if (errors.length > 0) {
        showError(errors.join('; '));
        return false;
    }

    return true;
}


/**
 * Show error message
 * @param {string} message - Error message
 */
function showError(message) {
    const errorAlert = document.getElementById('errorAlert');
    const errorMessage = document.getElementById('errorMessage');

    if (errorAlert && errorMessage) {
        errorMessage.textContent = message;
        errorAlert.style.display = 'block';

        // Auto-hide after 10 seconds
        setTimeout(() => {
            errorAlert.style.display = 'none';
        }, 10000);
    }

    console.error('Object Finder Error:', message);
}

/**
 * Update main loading message
 * @param {string} message - Loading message to display
 */
function updateMainLoadingMessage(message) {
    const loadingMessage = document.getElementById('loadingMessage');
    if (loadingMessage) {
        loadingMessage.textContent = message;
    }
}

/**
 * Show loading step indicator
 * @param {string} message - Loading message
 * @param {number} step - Current step (1-2)
 */
function showLoadingStep(message, step) {
    // Create or update loading indicator
    let loadingDiv = document.getElementById('step-loading-indicator');
    if (!loadingDiv) {
        loadingDiv = document.createElement('div');
        loadingDiv.id = 'step-loading-indicator';
        loadingDiv.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: rgba(0, 123, 255, 0.9);
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            z-index: 1050;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            min-width: 280px;
        `;
        document.body.appendChild(loadingDiv);
    }

    loadingDiv.innerHTML = `
        <div style="display: flex; align-items: center;">
            <div style="margin-right: 12px;">
                <i class="fas fa-spinner fa-spin"></i>
            </div>
            <div>
                <div style="font-weight: 600;">Step ${step} of 2</div>
                <div style="font-size: 12px; opacity: 0.9;">${message}</div>
            </div>
        </div>
    `;

    loadingDiv.style.display = 'block';
}

/**
 * Hide step loading indicators
 */
function hideLoadingSteps() {
    const loadingDiv = document.getElementById('step-loading-indicator');
    if (loadingDiv) {
        // Fade out effect
        loadingDiv.style.transition = 'opacity 0.3s ease';
        loadingDiv.style.opacity = '0';
        setTimeout(() => {
            loadingDiv.style.display = 'none';
            loadingDiv.style.opacity = '1';
        }, 300);
    }
}

/**
 * Check and load search parameters from URL
 */
function checkAndLoadFromURL() {
    const urlParams = urlSerializer.deserializeFromURL();

    if (urlParams && urlParams.isSharedLink) {
        console.log('Loading search from shared URL:', urlParams);

        // Validate parameters
        const validation = urlSerializer.validateParameters(urlParams);
        if (!validation.valid) {
            showError('Invalid URL parameters: ' + validation.errors.join('; '));
            return;
        }

        // Populate form fields
        populateFormFromParams(urlParams);

        // Show message about loaded search
        const description = urlSerializer.getSearchDescription(urlParams);
        showInfo(`Loaded shared search: ${description}`);
    }
}

/**
 * Populate form fields from parameters
 * @param {Object} params - Search parameters
 */
function populateFormFromParams(params) {
    if (params.latitude !== undefined) {
        document.getElementById('observerLatitude').value = params.latitude;
    }
    if (params.longitude !== undefined) {
        document.getElementById('observerLongitude').value = params.longitude;
    }
    if (params.observationDate) {
        document.getElementById('observationDate').value = params.observationDate;
    }
    if (params.azimuth !== undefined) {
        document.getElementById('targetAzimuth').value = params.azimuth;
        document.getElementById('azimuthSlider').value = params.azimuth;
    }
    if (params.minAltitude !== undefined) {
        document.getElementById('minAltitude').value = params.minAltitude;
        document.getElementById('minAltitudeSlider').value = params.minAltitude;
    }
    if (params.maxAltitude !== undefined) {
        document.getElementById('maxAltitude').value = params.maxAltitude;
        document.getElementById('maxAltitudeSlider').value = params.maxAltitude;
    }
    if (params.fieldOfView !== undefined) {
        document.getElementById('fieldOfView').value = params.fieldOfView;
        document.getElementById('fovSlider').value = params.fieldOfView;
    }
    if (params.minMagnitude !== undefined) {
        document.getElementById('minMagnitude').value = params.minMagnitude;
    }
    if (params.maxMagnitude !== undefined) {
        document.getElementById('maxMagnitude').value = params.maxMagnitude;
    }
    if (params.minDuration !== undefined) {
        document.getElementById('minDuration').value = params.minDuration;
    }
    if (params.moonAvoidance !== undefined) {
        document.getElementById('moonAvoidance').value = params.moonAvoidance;
    }

    // Trigger direction selector update if available
    if (objectFinder?.directionSelector) {
        objectFinder.directionSelector.updateDirectionDisplay();
        objectFinder.directionSelector.updateCompass();
    }
}

/**
 * Handle save configuration button click
 */
function handleSaveConfig() {
    const modal = new bootstrap.Modal(document.getElementById('saveConfigModal'));
    document.getElementById('configName').value = '';
    modal.show();
}

/**
 * Confirm save configuration
 */
function confirmSaveConfig() {
    const name = document.getElementById('configName').value.trim();

    if (!name) {
        showModalMessage('saveConfigMessage', 'Please enter a configuration name', 'warning');
        return;
    }

    const formData = collectFormData();
    const success = configManager.saveConfiguration(name, formData);

    if (success) {
        showModalMessage('saveConfigMessage', 'Configuration saved successfully!', 'success');
        setTimeout(() => {
            bootstrap.Modal.getInstance(document.getElementById('saveConfigModal')).hide();
        }, 1500);
    } else {
        showModalMessage('saveConfigMessage', 'Failed to save configuration', 'danger');
    }
}

/**
 * Handle load configuration button click
 */
function handleLoadConfig() {
    const modal = new bootstrap.Modal(document.getElementById('loadConfigModal'));
    populateConfigList();
    modal.show();
}

/**
 * Populate configuration list in load modal
 */
function populateConfigList() {
    const configList = document.getElementById('configList');
    const noConfigsMessage = document.getElementById('noConfigsMessage');
    const configs = configManager.getSavedConfigurations();
    const configNames = Object.keys(configs);

    if (configNames.length === 0) {
        configList.style.display = 'none';
        noConfigsMessage.style.display = 'block';
        return;
    }

    configList.style.display = 'block';
    noConfigsMessage.style.display = 'none';

    let html = '';
    configNames.forEach(name => {
        const config = configs[name];
        const savedDate = new Date(config.savedDate).toLocaleString();

        html += `
            <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                        <h6 class="mb-1">${name}</h6>
                        <small class="text-muted">
                            Saved: ${savedDate}<br>
                            Location: ${config.latitude?.toFixed(2)}, ${config.longitude?.toFixed(2)}<br>
                            Direction: ${config.azimuth}° (${urlSerializer.azimuthToDirection(config.azimuth)})
                        </small>
                    </div>
                    <div class="btn-group-vertical btn-group-sm ms-2">
                        <button class="btn btn-primary" onclick="window.loadConfigByName('${name}')">
                            <i class="fas fa-upload"></i> Load
                        </button>
                        <button class="btn btn-outline-danger" onclick="window.deleteConfigByName('${name}')">
                            <i class="fas fa-trash"></i>
                        </button>
                    </div>
                </div>
            </div>
        `;
    });

    configList.innerHTML = html;
}

/**
 * Load configuration by name
 * @param {string} name - Configuration name
 */
window.loadConfigByName = function(name) {
    const config = configManager.loadConfiguration(name);

    if (config) {
        populateFormFromParams(config);
        showModalMessage('loadConfigMessage', `Configuration "${name}" loaded successfully!`, 'success');
        setTimeout(() => {
            bootstrap.Modal.getInstance(document.getElementById('loadConfigModal')).hide();
        }, 1500);
    } else {
        showModalMessage('loadConfigMessage', 'Failed to load configuration', 'danger');
    }
};

/**
 * Delete configuration by name
 * @param {string} name - Configuration name
 */
window.deleteConfigByName = function(name) {
    if (confirm(`Delete configuration "${name}"?`)) {
        const success = configManager.deleteConfiguration(name);
        if (success) {
            populateConfigList();
            showModalMessage('loadConfigMessage', `Configuration "${name}" deleted`, 'info');
        }
    }
};

/**
 * Handle clear all configs
 */
function handleClearAllConfigs() {
    if (confirm('Delete ALL saved configurations? This cannot be undone.')) {
        const success = configManager.clearAllConfigurations();
        if (success) {
            populateConfigList();
            showModalMessage('loadConfigMessage', 'All configurations cleared', 'info');
        }
    }
}

/**
 * Handle share search button click
 */
function handleShareSearch() {
    if (!lastSearchParams) {
        showError('No search results to share. Please run a search first.');
        return;
    }

    const shareUrl = urlSerializer.serializeToURL(lastSearchParams, lastSearchResults);
    document.getElementById('shareUrl').value = shareUrl;

    const modal = new bootstrap.Modal(document.getElementById('shareLinkModal'));
    modal.show();
}

/**
 * Copy share URL to clipboard
 */
async function copyShareUrl() {
    const shareUrl = document.getElementById('shareUrl').value;
    const success = await urlSerializer.copyToClipboard(shareUrl);

    const messageDiv = document.getElementById('shareMessage');
    if (success) {
        messageDiv.style.display = 'block';
        setTimeout(() => {
            messageDiv.style.display = 'none';
        }, 3000);
    } else {
        alert('Failed to copy URL to clipboard');
    }
}

/**
 * Show modal message
 * @param {string} elementId - Message element ID
 * @param {string} message - Message text
 * @param {string} type - Alert type (success, warning, danger, info)
 */
function showModalMessage(elementId, message, type) {
    const messageDiv = document.getElementById(elementId);
    if (messageDiv) {
        messageDiv.className = `alert alert-${type}`;
        messageDiv.textContent = message;
        messageDiv.style.display = 'block';
    }
}

/**
 * Show info message
 * @param {string} message - Info message
 */
function showInfo(message) {
    const infoDiv = document.createElement('div');
    infoDiv.className = 'alert alert-info alert-dismissible fade show';
    infoDiv.style.cssText = 'position: fixed; top: 80px; right: 20px; z-index: 1050; max-width: 400px;';
    infoDiv.innerHTML = `
        <i class="fas fa-info-circle me-2"></i>${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    document.body.appendChild(infoDiv);

    setTimeout(() => {
        infoDiv.remove();
    }, 5000);
}

/**
 * Initialize when DOM is loaded
 */
document.addEventListener('DOMContentLoaded', initializeObjectFinder);

// Export for debugging and HTML access
window.objectFinder = objectFinder;
window.resultsDisplay = resultsDisplay;